import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { CCTVCamera, DomeCCTV, PTZCamera, DVRUnit, ShieldIcon, InstallationIllustration } from '../components/Illustrations';
import LiveMonitor from '../components/LiveMonitor';
import ReferralProgram from '../components/ReferralProgram';
import './Home.css';

const Home = () => {
  const { t } = useTranslation();
  const revealRefs = useRef([]);

  const stats = [
    { number: '10+', label: t('home.hero.yearsExperience') },
    { number: '500+', label: t('home.hero.installations') },
    { number: '100+', label: t('home.hero.happyClients') },
    { number: '24/7', label: t('home.hero.support') },
  ];

  const services = [
    {
      icon: <CCTVCamera size={80} />,
      title: t('home.services.cctv.title'),
      desc: t('home.services.cctv.desc'),
      link: '/services',
    },
    {
      icon: <DVRUnit size={100} />,
      title: t('home.services.dvr.title'),
      desc: t('home.services.dvr.desc'),
      link: '/services',
    },
    {
      icon: <DomeCCTV size={80} />,
      title: t('home.services.amc.title'),
      desc: t('home.services.amc.desc'),
      link: '/services',
    },
    {
      icon: <PTZCamera size={80} />,
      title: t('home.services.gem.title'),
      desc: t('home.services.gem.desc'),
      link: '/gem',
    },
  ];

  const testimonials = [
    { name: 'Suresh Patil', role: 'Factory Owner, Kagal', text: 'ShivShri Enterprises installed 12 cameras in my factory quickly and professionally. Excellent service and very honest pricing. Highly recommended!', initials: 'SP' },
    { name: 'Laxmi Desai', role: 'School Principal, Gargoti', text: 'They installed cameras throughout our school. Very professional team, completed in one day and explained everything clearly. Great support too.', initials: 'LD' },
    { name: 'Rajendra More', role: 'Shop Owner, Kolhapur', text: 'Got my shop CCTV done by them. Price was very fair, quality cameras and after service is excellent. Have already referred them to 5 others!', initials: 'RM' },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => e.isIntersecting && e.target.classList.add('visible')),
      { threshold: 0.08 }
    );
    revealRefs.current.forEach(el => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const addReveal = (el) => { if (el && !revealRefs.current.includes(el)) revealRefs.current.push(el); };

  return (
    <div className="home">
      {/* ── HERO ── */}
      <section className="hero">
        <div className="hero__bg" />
        <div className="container hero__inner">
          <div className="hero__content">
            <div className="hero__badge">
              <span className="hero__badge-dot" />
              {t('home.hero.badge')}
            </div>
            
            {/* Vocal for Local Badge */}
            <div className="hero__vocal-local">
              <span className="vocal-flag">🇮🇳</span>
              <span className="vocal-text">{t('home.hero.vocalForLocal') || 'Vocal for Local'}</span>
              <span className="vocal-divider">|</span>
              <span className="vocal-subtext">{t('home.hero.kolhapursOwn') || "Kolhapur's Own Since 10+ Years"}</span>
            </div>
            
            <h1 className="hero__title">
              {t('home.hero.title1')}<br />
              {t('home.hero.title2')} <em>{t('home.hero.title3')}</em><br />
              {t('home.hero.title4')}
            </h1>
            <p className="hero__sub">
              {t('home.hero.subtitle')}
            </p>
            <div className="hero__actions">
              <Link to="/order" className="btn btn-green">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.93 1.18 2 2 0 012.92 1h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L6.91 8.9A16 16 0 0015.1 17.1l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 17z"/></svg>
                {t('home.hero.getFreeQuote')}
              </Link>
              <Link to="/services" className="btn btn-outline">{t('home.hero.ourServices')}</Link>
              <Link to="/gem" className="btn btn-saffron">
                {t('home.hero.gemPortal')}
              </Link>
            </div>
            <div className="hero__trust">
              {[t('home.hero.licensedTechnicians'), t('home.hero.warranty'), t('home.hero.freeSurvey'), t('home.hero.gemRegistered')].map(trust => (
                <div key={trust} className="hero__trust-pill">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                  {trust}
                </div>
              ))}
            </div>
          </div>

          <div className="hero__visual">
            <div className="hero__mockup">
              <div className="hero__cam-grid">
                {['CAM 01 · Main Gate', 'CAM 02 · Office', 'CAM 03 · Warehouse', 'CAM 04 · Parking'].map((label) => (
                  <div key={label} className="hero__cam-feed">
                    <div className="hero__cam-live">LIVE</div>
                    <CCTVCamera size={50} color="rgba(255,255,255,0.3)" />
                    <div className="hero__cam-label">{label}</div>
                  </div>
                ))}
              </div>
              <div className="hero__mockup-footer">
                {stats.map(s => (
                  <div key={s.label} className="hero__stat">
                    <span className="hero__stat-num">{s.number}</span>
                    <span className="hero__stat-label">{s.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="hero__float hero__float--1">
              <ShieldIcon size={32} color="#1e8a4c" />
              <div>
                <div className="hero__float-title">{t('home.hero.gemRegisteredFull')}</div>
                <div className="hero__float-sub">{t('home.hero.govtApproved')}</div>
              </div>
            </div>
            <div className="hero__float hero__float--2">
              <div className="hero__float-dot" />
              <div>
                <div className="hero__float-title">{t('home.hero.systemsOnline')}</div>
                <div className="hero__float-sub">{t('home.hero.realTimeMonitoring')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CLIENTS STRIP ── */}
      <div className="clients-strip">
        <div className="container clients-strip__inner">
          <span className="clients-strip__label">{t('home.clientsStrip.trustedBy')}</span>
          <div className="clients-strip__logos">
            {[t('home.clientsStrip.govtSchools'), t('home.clientsStrip.banks'), t('home.clientsStrip.factories'), t('home.clientsStrip.hospitals'), t('home.clientsStrip.societies'), t('home.clientsStrip.retail')].map(c => (
              <span key={c} className="clients-strip__logo">{c}</span>
            ))}
          </div>
        </div>
      </div>

      {/* ── SERVICES ── */}
      <section className="section-padding" style={{ background: 'var(--white)' }}>
        <div className="container">
          <div className="home-section-header" ref={addReveal}>
            <div className="reveal" ref={addReveal}>
              <div className="section-badge">{t('home.services.badge')}</div>
              <h2>{t('home.services.title')}</h2>
            </div>
            <p className="home-section-sub reveal" ref={addReveal}>
              {t('home.services.subtitle')}
            </p>
          </div>
          <div className="home-services-grid">
            {services.map((s, i) => (
              <Link to={s.link} key={s.title} className="home-service-card reveal" ref={addReveal} style={{ transitionDelay: `${i * 0.1}s` }}>
                <div className="home-service-icon">{s.icon}</div>
                <h3 className="home-service-title">{s.title}</h3>
                <p className="home-service-desc">{s.desc}</p>
                <div className="home-service-link">
                  {t('home.services.learnMore')}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── LIVE MONITOR DEMO ── */}
      <section className="section-padding" style={{background: 'var(--off-white)'}}>
        <div className="container">
          <div className="text-center reveal" ref={addReveal} style={{marginBottom: '3rem'}}>
            <div className="section-badge green">{t('home.liveMonitor.badge') || '🎥 Live CCTV Surveillance'}</div>
            <h2>{t('home.liveMonitor.title') || 'Professional Security Monitoring System'}</h2>
            <p style={{color: 'var(--text-mid)', maxWidth: '700px', margin: '0.5rem auto 0', fontSize: '1rem', lineHeight: '1.7'}}>
              {t('home.liveMonitor.subtitle') || 'Experience real CCTV surveillance with our advanced monitoring systems. Watch your property 24/7 with crystal-clear footage, motion detection, and instant alerts - just like these live camera feeds below!'}
            </p>
          </div>
          <div className="reveal" ref={addReveal}>
            <LiveMonitor />
          </div>
        </div>
      </section>

      {/* ── ABOUT STRIP ── */}
      <section className="about-strip section-padding">
        <div className="container about-strip__inner">
          <div className="about-strip__visual reveal-left" ref={addReveal}>
            <InstallationIllustration width={420} height={300} />
          </div>
          <div className="about-strip__content reveal-right" ref={addReveal}>
            <div className="section-badge green">{t('home.about.badge')}</div>
            <h2>{t('home.about.title')}</h2>
            <p>
              {t('home.about.desc1')}
            </p>
            <p>
              {t('home.about.desc2')}
            </p>
            <div className="about-strip__highlights">
              {[
                { icon: '📍', text: t('home.about.headquartered') },
                { icon: '🏛️', text: t('home.about.gemSupplier') },
                { icon: '🔧', text: t('home.about.certified') },
                { icon: '⭐', text: t('home.about.excellence') },
              ].map(h => (
                <div key={h.text} className="about-strip__highlight">
                  <span>{h.icon}</span> {h.text}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '2rem' }}>
              <Link to="/about" className="btn btn-primary">{t('home.about.knowMore')}</Link>
              <Link to="/gem" className="btn btn-saffron">{t('home.about.viewGem')}</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="section-padding" style={{ background: 'var(--blue-pale)', borderTop: '1px solid var(--border)' }}>
        <div className="container">
          <div className="text-center reveal" ref={addReveal}>
            <div className="section-badge" style={{ marginBottom: '0.5rem' }}>{t('home.testimonials.badge')}</div>
            <h2>{t('home.testimonials.title')}</h2>
          </div>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, i) => (
              <div key={testimonial.name} className="testimonial-card reveal" ref={addReveal} style={{ transitionDelay: `${i * 0.12}s` }}>
                <div className="testimonial-stars">★★★★★</div>
                <p className="testimonial-text">"{testimonial.text}"</p>
                <div className="testimonial-author">
                  <div className="testimonial-avatar">{testimonial.initials}</div>
                  <div>
                    <div className="testimonial-name">{testimonial.name}</div>
                    <div className="testimonial-role">{testimonial.role}</div>
                  </div>
                </div>
                <div className="testimonial-verified">{t('home.testimonials.verified')}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── REFERRAL PROGRAM ── */}
      <ReferralProgram />

      {/* ── CTA BANNER ── */}
      <section className="cta-banner">
        <div className="container cta-banner__inner">
          <div>
            <h2 className="cta-banner__title">{t('home.cta.title')}</h2>
            <p className="cta-banner__sub">{t('home.cta.subtitle')}</p>
          </div>
          <div className="cta-banner__actions">
            <Link to="/order" className="btn btn-green">{t('home.cta.getFreeQuote')}</Link>
            <a href="tel:+919421722575" className="btn btn-outline-white">{t('home.cta.callNow')}</a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
